import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const HEADER_SELECT_ALL_SOURCES = new Set([
  "uiSelectAll",
  "uiSelectAllFiltered",
  "uiSelectAllCurrentPage",
]);

const getGridApi = (gridApiRef) =>
  gridApiRef?.current?.api ?? gridApiRef?.current ?? null;

/**
 * Selection helper for grids that only render one page of server/client data at a time.
 * Header "select all" applies to every row in the current result set, not just the page.
 *
 * @param {Object} options
 * @param {number} options.totalItems - Total rows in the current filtered result set
 * @param {() => Promise<Array>|Array} [options.fetchAllRows] - Returns all rows when select-all is active
 * @param {(row: Object) => string|number} [options.getRowId]
 * @param {string} [options.entityName]
 * @param {Array} [options.rowData] - Current page rows; used to restore checkboxes after page changes
 * @param {import("react").RefObject} [options.gridApiRef]
 * @param {*} [options.resetKey] - Clears selection when this value changes
 */
export function usePaginatedGridSelection({
  totalItems = 0,
  fetchAllRows,
  getRowId = (row) => row?.id,
  entityName = "item",
  rowData = [],
  gridApiRef,
  resetKey,
}) {
  const [selectAllPages, setSelectAllPages] = useState(false);
  const [deselectedRowIds, setDeselectedRowIds] = useState(() => new Set());
  const [selectedRows, setSelectedRows] = useState([]);
  const selectAllPagesRef = useRef(selectAllPages);
  const deselectedRowIdsRef = useRef(deselectedRowIds);
  const resetKeyRef = useRef(resetKey);

  selectAllPagesRef.current = selectAllPages;
  deselectedRowIdsRef.current = deselectedRowIds;

  const selectedCount = useMemo(() => {
    if (selectAllPages) {
      return Math.max(0, totalItems - deselectedRowIds.size);
    }
    return selectedRows.length;
  }, [selectAllPages, totalItems, deselectedRowIds, selectedRows.length]);

  const bannerText = useMemo(() => {
    if (selectedCount <= 0) {
      return null;
    }
    return `${selectedCount} ${entityName}${selectedCount === 1 ? "" : "s"} selected`;
  }, [selectedCount, entityName]);

  const clearSelection = useCallback(
    (apiOverride) => {
      const api = apiOverride ?? getGridApi(gridApiRef);
      api?.deselectAll?.();
      setSelectAllPages(false);
      setDeselectedRowIds(new Set());
      setSelectedRows([]);
    },
    [gridApiRef],
  );

  useEffect(() => {
    if (resetKeyRef.current === resetKey) {
      return;
    }
    resetKeyRef.current = resetKey;
    clearSelection();
  }, [resetKey, clearSelection]);

  const syncPageSelection = useCallback(
    (api) => {
      if (!api || !selectAllPagesRef.current) {
        return;
      }
      api.forEachNode((node) => {
        if (!node.data) {
          return;
        }
        const id = getRowId(node.data);
        node.setSelected(!deselectedRowIdsRef.current.has(id));
      });
    },
    [getRowId],
  );

  useEffect(() => {
    syncPageSelection(getGridApi(gridApiRef));
  }, [rowData, syncPageSelection, gridApiRef]);

  const onRowSelected = useCallback(
    (event) => {
      if (!selectAllPagesRef.current) {
        return;
      }
      if (HEADER_SELECT_ALL_SOURCES.has(event.source)) {
        return;
      }

      const rowId = getRowId(event.node?.data);
      if (rowId == null) {
        return;
      }

      setDeselectedRowIds((prev) => {
        const next = new Set(prev);
        if (event.node.isSelected()) {
          next.delete(rowId);
        } else {
          next.add(rowId);
        }
        return next;
      });
    },
    [getRowId],
  );

  const onSelectionChanged = useCallback(
    (event) => {
      const { api, source } = event;

      if (HEADER_SELECT_ALL_SOURCES.has(source)) {
        const displayedCount = api.getDisplayedRowCount();
        const displayedSelected = api.getSelectedRows();

        if (displayedCount > 0 && displayedSelected.length === displayedCount) {
          setSelectAllPages(true);
          setDeselectedRowIds(new Set());
          setSelectedRows([]);
        } else {
          clearSelection(api);
        }
        return;
      }

      if (selectAllPagesRef.current) {
        return;
      }

      setSelectedRows(api.getSelectedRows());
    },
    [clearSelection],
  );

  const resolveSelectedRows = useCallback(async () => {
    if (!selectAllPages) {
      return selectedRows;
    }

    if (!fetchAllRows) {
      return selectedRows;
    }

    const allRows = await fetchAllRows();
    return allRows.filter((row) => !deselectedRowIds.has(getRowId(row)));
  }, [
    selectAllPages,
    fetchAllRows,
    selectedRows,
    deselectedRowIds,
    getRowId,
  ]);

  return {
    selectedRows,
    selectedCount,
    selectAllPages,
    deselectedRowIds,
    bannerText,
    onSelectionChanged,
    onRowSelected,
    clearSelection,
    resolveSelectedRows,
  };
}
